<script setup lang="ts">
  import { ref } from 'vue'
  import { onClickOutside } from '@vueuse/core'
  import { useProjectsMenuStore } from '@/stores/list-item-menu'
  import { usePopupStore } from '@/stores/popup'
  import { useLoaderStore } from '@/stores/loader'
  import { useProjectsStore } from '@/stores/projects'
  import { useModalStore } from '@/stores/modal'
  import { useQRCodeStore } from '@/stores/qr-code'
  import { useSnackbarStore } from '@/stores/snackbar'
  import { useLiveEbookStore } from '@/stores/live-ebook'
  import { useUserStore } from '@/stores/user'
  import { useFlipbooksStore } from '@/stores/flipbooks'
  import { useBrowserStore } from '@/stores/browser'
  import { useFileInfoStore } from '@/stores/file-info'
  import { useUpgradeStore } from '@/stores/upgrade'
  import { useRouter } from 'vue-router'
  import { Products } from '@/helpers/product'
  import { track } from '@/helpers/mixpanelDes'
  import { getStatusMessage } from '@/helpers/http'
  import { reportError } from '@/helpers/reportError'
  import DesMenuOptions from '@/components/menu/DesMenuOptions.vue'
  import DesMenuItem from '@/components/menu/DesMenuItem.vue'
  import DesMenuSeparator from '@/components/menu/DesMenuSeparator.vue'
  import type { TProject } from '@/types/projects'
  import type { TPublishMetadata } from '@/types/live-ebook'
  import type { TAccountType } from '@/types/user'
  import type { TFlipbook, TJsonFlipobookData } from '@/types/flipbooks'
  import type { TCoverConfirmDetailsResult } from '@/components/modal/DesModalCoverConfirmDetails.vue'

  const menu = useProjectsMenuStore()
  const projects = useProjectsStore()
  const popup = usePopupStore()
  const loader = useLoaderStore()
  const modal = useModalStore()
  const snack = useSnackbarStore()
  const qr = useQRCodeStore()
  const liveEbook = useLiveEbookStore()
  const user = useUserStore()
  const flipbook = useFlipbooksStore()
  const browser = useBrowserStore()
  const upgrade = useUpgradeStore()
  const fileInfo = useFileInfoStore()
  const router = useRouter()

  const accountType = ref<TAccountType>(user.accountType)

  const target = ref<HTMLElement | null>(null)
  onClickOutside(target, () => menu.item && menu.close())

  async function embedFlipbook() {
    if (!menu?.item?.id || isNaN(menu.item.id)) {
      return
    }
    loader.show('Getting data...')
    let msg = 'There was an error, please try again later!'
    try {
      const createFlipbookData: TFlipbook | null = await flipbook.createFlipbook(menu.item.id)
      loader.hide()
      if (createFlipbookData.status === 'error') {
        msg = createFlipbookData.message ?? msg
        snack.add(msg, { type: 'danger', permanent: true })
        return
      }
      modal.open(() => import('@/components/modal/DesModalFlipbookEmbed.vue'), {
        createFlipbookData,
      })
    } catch (e: any) {
      loader.hide()
      msg = e.message ?? msg
      snack.add(msg, { type: 'danger', permanent: true })
    }
  }

  async function generateQR() {
    if (!menu?.item?.id || isNaN(menu.item.id)) {
      return
    }

    const projectId = menu.item.id
    loader.show('Getting data...')

    const formats = await qr.attemptToGetFormats(projectId)
    loader.hide()

    if (formats.length) {
      modal.open(() => import('@/components/modal/DesModalQR.vue'), {
        formats,
      })
    }
  }

  async function editLiveEbookOptions() {
    if (!menu?.item?.id) return

    const projectId: number = menu.item.id

    loader.show('Getting data...')
    const publishMetadata: TPublishMetadata | null = await liveEbook.getPublishMetadata(projectId)
    loader.hide()

    modal.open(() => import('@/components/modal/DesModalLiveEbookOptions.vue'), {
      projectId,
      publishMetadata,
    })
  }

  async function share() {
    if (!menu.item) return

    const pdfUrl = await getPdfUrl()
    if (!pdfUrl?.length) {
      snack.add('No PDF to share')
      return
    }

    await modal.open(() => import('@/components/modal/DesModalShareProject.vue'), {
      project: menu.item,
      pdfUrl: pdfUrl,
    })
  }

  async function duplicateProject() {
    if (!menu.item) return

    const name = await popup.prompt('Duplicate project', {
      initial: `${menu.item.name} copy`,
      content: 'Enter copy name',
      labelConfirm: 'Duplicate',
      align: 'left',
    })

    if (name) {
      const response = await projects.duplicateProject(menu.item, name)

      if (response.status !== 'success') {
        snack.add(response.message)
      }
    }
  }

  async function openNewWindowTab(url: string) {
    let win = window.open(url, '_blank')
    if (!win) {
      const winResult = await popup.confirm('It looks like your browser blocked a popup/new tab for opening.', {
        labelCancel: 'Cancel',
        labelConfirm: 'Open',
      })

      if (winResult !== false) {
        win = window.open(url, '_blank')
        win?.focus()
      }
      return
    }
    win.focus()
  }

  async function regeneratePDF(project: TProject): Promise<string> {
    const result = await projects.regeneratePDF(project)
    return result?.pdf?.url ?? ''
  }

  async function openPDFInFlipbook() {
    if (!menu?.item?.id) {
      return
    }

    const errorGettingFlipbookUrl: string = 'Error parsing flipbook'
    const errorNoPdf: string = 'You need to export the PDF first'

    loader.show('Getting data...')

    try {
      const createFlipbookData: TFlipbook | null = await flipbook.createFlipbook(menu.item.id)
      const flipbookData: TJsonFlipobookData = createFlipbookData?.flipbook
        ? JSON.parse(createFlipbookData.flipbook)
        : null
      if (!flipbookData?.url?.length) {
        snack.add(errorNoPdf, { type: 'danger', permanent: true })
        loader.hide()
        return
      }

      const url: string =
        createFlipbookData?.flipbook?.length && createFlipbookData?.status === 'success'
          ? await flipbook.getFlipbookInfo(createFlipbookData)
          : ''
      if (!url.length) {
        snack.add(errorGettingFlipbookUrl, { type: 'danger' })
        loader.hide()
        return
      }

      loader.hide()
      await browser.openNewWindowTab(url)
    } catch (e: any) {
      loader.hide()
      const message = e?.message
      let displayMsg
      if (message?.includes('The PDF is still being generated')) {
        displayMsg = message
      } else if (message?.includes('Failed to download external file')) {
        displayMsg = 'Error fetching PDF for this project'
      } else if (message?.includes('pdf not created')) {
        displayMsg = errorNoPdf
      } else if (e.status === 401) {
        let response = e.response
        let msgMsg
        try {
          response = JSON.parse(response)
          msgMsg = response.message
        } catch {
          msgMsg = response
        }
        displayMsg = msgMsg
      } else {
        displayMsg = errorGettingFlipbookUrl
      }
      snack.add(displayMsg, { type: 'danger', permanent: true })
    }
  }

  async function getPdfUrl(): Promise<string> {
    if (!menu.item) return ''

    const pdfs = await projects.getPDFs(menu.item)
    let pdfLink = ''

    if (pdfs.pdfs.length === 0 || !pdfs.pdfs[0]?.url?.length) {
      const result = await popup.confirm('Your project has no PDF published. Would you like to publish the PDF?', {
        content: 'Projects larger than 60 pages may take a few minutes to produce.',
        labelCancel: 'Cancel',
        labelConfirm: 'Publish the PDF',
      })

      if (result !== false) {
        pdfLink = await regeneratePDF(menu.item)
      }
    } else if (pdfs.outdated === true) {
      const result = await popup.confirm(
        'Your project has been updated since the PDF was last published. Would you like to re-publish the PDF?',
        {
          content: 'Projects larger than 60 pages may take a few minutes to produce.',
          labelCancel: 'Cancel',
          labelConfirm: 'Update the PDF',
        },
      )

      if (result !== false) {
        pdfLink = await regeneratePDF(menu.item)
      } else {
        pdfLink = pdfs.pdfs[0].url
      }
    } else {
      pdfLink = pdfs.pdfs[0].url
    }

    return pdfLink
  }

  async function openPDF() {
    let pdfUrl = ''
    try {
      pdfUrl = await getPdfUrl()
    } catch (e: any) {
      const msg = e.message
      snack.add(msg, { type: 'danger', permanent: true })
    }
    if (pdfUrl?.length) {
      openNewWindowTab(pdfUrl)
    }
  }

  async function deleteProject() {
    if (!menu.item) return

    const result = await popup.confirm('Are you sure you want to delete this project?', {
      content: 'This action is irreversible.',
      labelCancel: 'Cancel',
      labelConfirm: 'Delete',
    })

    if (result !== false) {
      await projects.deleteProject(menu.item)
    }
  }

  async function moveToFolder() {
    await modal.open<number | undefined>(() => import('@/components/modal/DesModalAddToFolder.vue'), {
      project: menu.item,
      section: projects.section,
    })
  }

  async function generateCover() {
    if (!menu.item) return

    if (!user.isProOrHigherAccount()) {
      upgrade.showUpgradeModal('project-context-menu', Products.Pro)
      return
    }
    track('projects-ebook-menu', { label: 'Generate cover' })

    const project = await projects.fetchProject(menu.item.slug_id)
    let confirmResult: TCoverConfirmDetailsResult | undefined

    //when less than 24 pages, skip the details modal and use default settings. we can only create Kindle.
    if (project && (project?.pages_count || 0) < 24) {
      confirmResult = {
        project,
        trimSize: {
          height: 298,
          name: 'A4',
          width: 210,
        },
      }
    } else {
      confirmResult = await modal.open<TCoverConfirmDetailsResult | undefined>(
        () => import('@/components/modal/DesModalCoverConfirmDetails.vue'),
        { project },
      )
    }

    if (!confirmResult) return

    const id = await modal.open<number | undefined>(() => import('@/components/modal/DesModalCoverFromProject.vue'), {
      project: confirmResult.project,
      trimSize: confirmResult.trimSize,
    })

    id && router.push(`/cover-editor/${id}`)
  }

  async function saveAsTemplate() {
    await modal.open(() => import('@/components/modal/DesModalSaveProjectAsTemplate.vue'), {
      project: menu.item,
    })
  }

  async function versionHistory() {
    await modal.open(() => import('@/components/modal/DesModalVersionHistory.vue'), {
      project: menu.item,
    })
  }

  async function legacyCover() {
    await modal.open(() => import('@/components/modal/DesModalCovers.vue'), {
      id: menu.item?.id,
    })
  }

  async function fileInformation() {
    if (!menu?.item) {
      return
    }

    const project: TProject = menu.item

    loader.show('Getting data...')
    let data
    try {
      data = await Promise.all([
        fileInfo.getProjectDraft(project),
        fileInfo.getShares(project),
        fileInfo.getProjectFolder(project),
        user.isAgencyAccount() ? projects.getClientProjects(project.id) : [],
        projects.fetchSections(),
      ])
    } catch (error) {
      reportError('project-file-information', error, { project_id: project.id })
      snack.add(getStatusMessage(error, 'Failed to load the file information.'), { type: 'danger', permanent: true })

      return
    } finally {
      loader.hide()
    }

    const [draft, shares, projectFolder, clientProjects] = data

    modal.open(() => import('@/components/modal/DesModalFileInfo.vue'), {
      item: project,
      draft: draft,
      shares: shares,
      clientProjects,
      projectFolder,
    })
  }
</script>

<template>
  <div
    class="absolute z-50"
    :style="{ left: menu.position.left + 'px', top: menu.position.top + 'px' }"
    ref="target"
    @click="menu.close()"
  >
    <DesMenuOptions :open="menu.opened" :left="menu.flip" :right="!menu.flip">
      <DesMenuItem
        v-if="accountType['3d_covers']"
        @click="legacyCover()"
        icon="fa-light fa-cube"
        event="projects-ebook-menu"
      >
        Cover & Mockup Creator
      </DesMenuItem>
      <DesMenuItem @click="openPDF()" icon="fa-light fa-file-pdf" event="projects-ebook-menu">Open PDF</DesMenuItem>
      <DesMenuItem @click="openPDFInFlipbook()" icon="fa-light fa-book-open" event="projects-ebook-menu">
        Open PDF in flipbook
      </DesMenuItem>
      <DesMenuItem
        v-if="accountType.live_ebook_export && user.user.permissions['export'] === 1"
        @click="editLiveEbookOptions()"
        icon="fa-light fa-pen"
        event="projects-ebook-menu"
      >
        Edit live ebook options
      </DesMenuItem>
      <DesMenuItem
        v-if="accountType.flipbook_publish && user.user.permissions['publish'] === 1"
        @click="embedFlipbook()"
        icon="fa-light fa-file-code"
        event="projects-ebook-menu"
      >
        Generate flipbook embed code
      </DesMenuItem>
      <DesMenuItem
        v-if="user.user.permissions['publish'] === 1"
        @click="generateQR()"
        icon="fa-light fa-qrcode"
        event="projects-ebook-menu"
      >
        Generate QR code
      </DesMenuItem>
      <DesMenuItem
        v-if="user?.isProOrHigherAccount()"
        @click="generateCover()"
        icon="fa-light fa-book"
        event="projects-ebook-menu"
        tip="Printing is available only for<br>books with at least 24 pages"
        :unlockedFor="user?.isProOrHigherAccount() ? undefined : Products.Pro"
      >
        Create a Print or Kindle Cover
      </DesMenuItem>
      <DesMenuItem
        v-if="user.user.permissions['publish'] === 1"
        @click="share()"
        icon="fa-light fa-share"
        event="projects-ebook-menu"
      >
        Share
      </DesMenuItem>
      <DesMenuSeparator />
      <DesMenuItem
        v-if="user.user.permissions['projects.update'] === 1"
        @click="moveToFolder()"
        icon="fa-light fa-folder-plus"
        event="projects-ebook-menu"
      >
        Move
      </DesMenuItem>
      <DesMenuItem
        v-if="accountType.create_templates && user.user.permissions['templates.create'] === 1"
        @click="saveAsTemplate()"
        icon="fa-light fa-file-image"
        event="projects-ebook-menu"
      >
        Save as template
      </DesMenuItem>
      <DesMenuItem
        v-if="user.user.permissions['projects.create'] === 1"
        @click="duplicateProject()"
        icon="fa-light fa-copy"
        event="projects-ebook-menu"
      >
        Duplicate
      </DesMenuItem>
      <DesMenuItem @click="versionHistory()" icon="fa-light fa-rotate-left" event="projects-ebook-menu">
        Version history
      </DesMenuItem>
      <DesMenuItem @click="fileInformation()" icon="fa-light fa-circle-info" event="projects-ebook-menu">
        Project information
      </DesMenuItem>
      <DesMenuSeparator />
      <DesMenuItem
        v-if="user.user.permissions['projects.delete'] === 1"
        @click="deleteProject()"
        icon="fa-light fa-trash-can"
        danger
        event="projects-ebook-menu"
      >
        Delete
      </DesMenuItem>
    </DesMenuOptions>
  </div>
</template>
